// frontend/src/utils/generateReceiptPDF.js
//
// The printable a client walks out with. Same visual language as
// generateDocumentPDF (logo, blue rule, footer) so a receipt and an invoice
// from the same workshop look like they came from the same place.
//
// Separate from generateDocumentPDF rather than a third `type` on it: a
// receipt has no line items, no VAT and no margin — it is one amount, one
// date, one signature line. Threading that through the invoice layout would
// mean branching almost every block in it.

import { jsPDF } from 'jspdf';

// Same grouping as the dashboard, and it already folds fr-FR grouping to a
// plain space -- jsPDF helvetica has no glyph for U+202F and prints a blank
// box mid-amount.
import { fmt as MONEY } from '../theme/iparc';

const METHOD_LABELS = {
  cash: 'Espèces',
  wave: 'Wave',
  orange_money: 'Orange Money',
  mtn_money: 'MTN Money',
  moov_money: 'Moov Money',
  bank_transfer: 'Virement bancaire',
  cheque: 'Chèque',
  card: 'Carte bancaire',
  other: 'Autre',
};

/**
 * The stamp. Rotated, outlined, semi-transparent — the look of something
 * pressed onto paper rather than a filled banner, which would read as part of
 * the layout and be ignored.
 *
 * Colour carries the meaning: red PAYÉ only when nothing is outstanding, amber
 * ACOMPTE for a part payment, grey ANNULÉ for a cancelled receipt. A red PAYÉ
 * on a half-settled job is a dispute waiting to happen.
 */
function drawStamp(doc, { text, rgb, x, y }) {
  const w = 62;
  const h = 20;
  const cx = x + w / 2;
  const cy = y + h / 2;
  const deg = 14; // tilted up to the right, the way a hand-held stamp lands

  let stateSaved = false;
  try {
    doc.saveGraphicsState();
    stateSaved = true;
    doc.setGState(new doc.GState({ opacity: 0.55 }));
  } catch (_) {
    // No transparency on this build. The stamp is still legible at full
    // opacity, so this must never be the thing that throws.
  }

  doc.setDrawColor(...rgb);
  doc.setTextColor(...rgb);
  doc.setLineWidth(1.6);

  // The border is drawn as four rotated lines rather than a rotated rect:
  // jsPDF only accepts an angle on text, so roundedRect/rect would come out
  // square while the label inside it sat at an angle.
  const rad = (-deg * Math.PI) / 180; // PDF user space has y increasing downward
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const corners = [
    [-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2],
  ].map(([px, py]) => [cx + px * cos - py * sin, cy + px * sin + py * cos]);

  for (let i = 0; i < corners.length; i += 1) {
    const [x1, y1] = corners[i];
    const [x2, y2] = corners[(i + 1) % corners.length];
    doc.line(x1, y1, x2, y2);
  }

  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(text, cx, cy + 2.5, { align: 'center', angle: deg });

  if (stateSaved) doc.restoreGraphicsState();
}

/**
 * @param {Object} receipt        PaymentReceipt, as returned by /api/receipts
 * @param {Object} issuerData     the workshop/supplier: businessName, address, phone, email, taxId, logo
 */
export function generateReceiptPDF(receipt, issuerData = {}) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;

  const primary = [41, 128, 185];
  const darkText = [30, 30, 30];
  const grayText = [120, 120, 120];
  const red = [200, 30, 30];
  const amber = [190, 130, 20];
  const grey = [130, 130, 130];

  const paid = Number(receipt?.amountPaid) || 0;
  const due = Number(receipt?.amountDue) || 0;
  const balance = due > 0 ? Math.max(0, due - paid) : 0;
  const cancelled = !!receipt?.cancelledAt;
  const state = cancelled ? 'cancelled' : (balance > 0 ? 'partial' : 'full');

  const docNumber =
    receipt?.documentNumber ||
    (Number.isInteger(receipt?.sequenceNumber)
      ? `R-${String(receipt.sequenceNumber).padStart(5, '0')}`
      : (receipt?._id ? `R-${String(receipt._id).slice(-8).toUpperCase()}` : 'R-----'));

  const paidDate = receipt?.paidAt ? new Date(receipt.paidAt) : new Date();
  const paidDateStr = paidDate.toLocaleDateString('fr-FR');

  // ── header ────────────────────────────────────────────────────────────────
  doc.setDrawColor(...primary);
  doc.setLineWidth(1);
  doc.line(margin, 42, pageWidth - margin, 42);

  if (issuerData?.logo) {
    try { doc.addImage(issuerData.logo, 'JPEG', margin, 5, 22, 22); } catch (_) { /* skip */ }
  }

  const infoX = issuerData?.logo ? margin + 26 : margin;
  doc.setTextColor(...darkText);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(issuerData?.businessName || issuerData?.name || 'Reçu', infoX, 14);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayText);
  if (issuerData?.address) doc.text(String(issuerData.address), infoX, 20);
  if (issuerData?.phone) doc.text(`Tel: ${issuerData.phone}`, infoX, 25);
  if (issuerData?.email) doc.text(String(issuerData.email), infoX, 30);
  if (issuerData?.taxId) doc.text(`N° TVA: ${issuerData.taxId}`, infoX, 35);

  doc.setTextColor(...primary);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('REÇU', pageWidth - margin, 16, { align: 'right' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.text(`N° ${docNumber}`, pageWidth - margin, 24, { align: 'right' });
  doc.text(`Date de paiement: ${paidDateStr}`, pageWidth - margin, 30, { align: 'right' });

  let y = 52;

  // ── payer & vehicle ───────────────────────────────────────────────────────
  doc.setFillColor(245, 247, 250);
  doc.roundedRect(margin, y, pageWidth - margin * 2, 22, 3, 3, 'F');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...grayText);
  doc.text('REÇU DE', margin + 5, y + 6);
  doc.text('VÉHICULE', pageWidth / 2 + 5, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.text(receipt?.clientName || '—', margin + 5, y + 13);
  if (receipt?.clientPhone) {
    doc.setFontSize(8);
    doc.setTextColor(...grayText);
    doc.text(String(receipt.clientPhone), margin + 5, y + 18);
  }

  doc.setFontSize(9);
  doc.setTextColor(...darkText);
  doc.text(receipt?.vehicleLabel || '—', pageWidth / 2 + 5, y + 13);
  if (receipt?.plateNumber) {
    doc.setFont('helvetica', 'bold');
    doc.text(String(receipt.plateNumber), pageWidth / 2 + 5, y + 18);
  }

  y += 34;

  // ── the amount, which is the whole document ───────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...grayText);
  doc.text('MONTANT REÇU', margin, y);

  y += 12;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(30);
  doc.setTextColor(...darkText);
  doc.text(`${MONEY(paid)} FCFA`, margin, y);

  y += 10;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...grayText);
  doc.text(`Mode de paiement: ${METHOD_LABELS[receipt?.method] || receipt?.method || '—'}`, margin, y);
  if (receipt?.reference) {
    y += 6;
    doc.text(`Référence: ${receipt.reference}`, margin, y);
  }

  // Only claim a balance when a total was actually stated. Silence beats a
  // confident "Reste à payer: 0" the workshop never asserted.
  if (due > 0) {
    y += 10;
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y - 4, pageWidth - margin, y - 4);

    doc.setFontSize(10);
    doc.setTextColor(...grayText);
    doc.text('Montant total dû', margin, y + 3);
    doc.setTextColor(...darkText);
    doc.text(`${MONEY(due)} FCFA`, pageWidth - margin, y + 3, { align: 'right' });

    y += 9;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...(balance > 0 ? amber : darkText));
    doc.text('Reste à payer', margin, y + 3);
    doc.text(`${MONEY(balance)} FCFA`, pageWidth - margin, y + 3, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    y += 6;
  }

  if (cancelled) {
    y += 12;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...red);
    doc.text(
      `Reçu annulé le ${new Date(receipt.cancelledAt).toLocaleDateString('fr-FR')}` +
      (receipt.cancelReason ? ` — ${receipt.cancelReason}` : ''),
      margin, y
    );
    doc.setFont('helvetica', 'normal');
  }

  if (receipt?.note) {
    y += 14;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...grayText);
    doc.text('Note:', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    const lines = doc.splitTextToSize(String(receipt.note), pageWidth - margin * 2);
    doc.text(lines, margin, y);
    y += lines.length * 5;
  }

  // ── stamp & signature, along the foot of the page ─────────────────────────
  // Anchored to the bottom margin rather than to the running `y`, so the stamp
  // lands in the same place on every receipt — a document whose stamp drifts
  // up and down with the length of a note does not read as pre-printed. The
  // Math.max keeps it below the content on the rare receipt with a long note.
  // +34 rather than +26: rotating the box makes it ~34mm tall on the page, so
  // the old clearance would have let a long note run into the top of it.
  const sigY = Math.max(y + 34, doc.internal.pageSize.getHeight() - 55);

  // Stamp bottom-left, signature bottom-right: "Signature et cachet" names
  // both, and this is the order a hand fills them in.
  drawStamp(doc, {
    text: state === 'full' ? 'PAYÉ' : state === 'partial' ? 'ACOMPTE' : 'ANNULÉ',
    rgb: state === 'full' ? red : state === 'partial' ? amber : grey,
    x: margin + 4,
    y: sigY - 16,
  });

  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.line(pageWidth - margin - 60, sigY, pageWidth - margin, sigY);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayText);
  doc.text('Signature et cachet', pageWidth - margin - 60, sigY + 5);

  const footerY = doc.internal.pageSize.getHeight() - 10;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...grayText);
  doc.text('Généré via 1PARC — www.1parc.com', pageWidth / 2, footerY, { align: 'center' });

  doc.save(`Recu-${docNumber}-${paidDateStr.replace(/\//g, '-')}.pdf`);
}

export { METHOD_LABELS };
